import type { Place, SavedPlace, Route, LocationFix } from "./types";
import type { Settings } from "./storage";
import { getDeviceId } from "./storage";
import { isGooglePlace } from "./search";
import { distanceMeters } from "./navigation";

export interface CarContext {
  version: 1;
  saved: SavedPlace[];
  destination: Place | null;
  avoidDangers: boolean;
  speechProvider: "f5" | "device";
  voiceAlerts: boolean;
  reportAlerts: boolean;
}
declare global {
  interface Window {
    DerechCar?: {
      updateContext(context: CarContext): void;
      updateNavigation?(navigation: CarNavigation): void;
      requestNavigationState?(): void;
    };
  }
}
export type CarNavigation = { version: 1; tripId: string; state: "stopped" } | {
  version: 1; tripId: string; state: "active"; destination: Place; deviceId: string;
  route: Route; fix: LocationFix | null; progressMeters: number;
};
export interface CarNavigationState {
  connected: boolean; active: boolean; tripId?: string; ended?: boolean; arrived?: boolean; fix?: LocationFix & { mock?: boolean };
  route?: Route;
}
/** An active trip is separate from a selected destination/route preview. */
export function carNavigation(tripId: string, destination: Place, route: Route, fix: LocationFix | null, progress: number): CarNavigation | null {
  const value: CarNavigation = {
    version: 1, tripId, state: "active", destination: place(destination), deviceId: getDeviceId(), fix,
    progressMeters: Math.max(0, Math.min(1, progress)) * route.geometry.slice(1).reduce((total, point, index) =>
      total + distanceMeters({ lat: route.geometry[index][1], lng: route.geometry[index][0] }, { lat: point[1], lng: point[0] }), 0),
    route: { id: route.id, distance: route.distance, duration: route.duration,
      geometry: route.geometry.map(p => [Number(p[0].toFixed(6)), Number(p[1].toFixed(6))]),
      steps: route.steps.map(s => ({ instruction: s.instruction, distance: s.distance, duration: s.duration,
        lat: s.lat, lng: s.lng, type: s.type, modifier: s.modifier })) },
  };
  return new TextEncoder().encode(JSON.stringify(value)).length <= 290_000 ? value : null;
}
function place(value: Place): Place {
  return {
    id: value.id.slice(0, 160),
    name: value.name.slice(0, 120),
    address: value.address.slice(0, 240),
    lat: value.lat,
    lng: value.lng,
  };
}
export function carContext(
  saved: SavedPlace[],
  destination: Place | null,
  settings: Pick<Settings, "avoidDangers" | "speechProvider" | "voiceAlerts" | "reportAlerts">,
): CarContext {
  const prioritized = [
    ...saved.filter((p) => p.kind === "home" || p.kind === "work"),
    ...saved.filter((p) => p.kind === "favorite"),
  ].filter((p) => !isGooglePlace(p));
  const result: CarContext = {
    version: 1,
    saved: prioritized.slice(0, 50).map((p) => ({ ...place(p), kind: p.kind })),
    destination:
      destination && !isGooglePlace(destination) ? place(destination) : null,
    avoidDangers: settings.avoidDangers,
    speechProvider: settings.speechProvider,
    voiceAlerts: settings.voiceAlerts,
    reportAlerts: settings.reportAlerts,
  };
  // Native iOS caps UTF-8 bytes, not JS characters. Keep room for bridge framing,
  // preserving the current destination/preferences and Home/Work before favorites.
  const encoder = new TextEncoder();
  while (
    result.saved.length &&
    encoder.encode(JSON.stringify(result)).length > 46_000
  )
    result.saved.pop();
  return result;
}
